import { MonitorCog, PanelLeft, RotateCcw, Smartphone, SunMoon } from "lucide-react";
import { TitleToolbar } from "@core/components/TitleToolbar";
import { useAppPreferences } from "@core/preferences/AppPreferencesProvider";

function SettingSelect({ label, description, value, onChange, children }) {
  return (
    <label className="settings-control-row">
      <span><strong>{label}</strong><small>{description}</small></span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>{children}</select>
    </label>
  );
}

function SettingToggle({ label, description, checked, onChange }) {
  return (
    <label className="settings-control-row">
      <span><strong>{label}</strong><small>{description}</small></span>
      <input className="vp-switch" type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    </label>
  );
}

export function AppearanceSettingsPage() {
  const { preferences, setPreference, resetLayoutPreferences } = useAppPreferences();
  return (
    <div className="settings-page-stack">
      <TitleToolbar
        density="compact"
        title="Sidebar and appbars"
        icon={PanelLeft}
      />
      <section className="settings-card">
        <div className="settings-card-header"><span className="settings-list-icon"><SunMoon /></span><div><h2>Theme</h2><p>Choose the application color mode.</p></div></div>
        <SettingSelect label="Color theme" description="Use dark, light, or your operating-system preference." value={preferences.theme} onChange={(value) => setPreference("theme", value)}>
          <option value="dark">Dark</option><option value="light">Light</option><option value="system">System</option>
        </SettingSelect>
      </section>
      <section className="settings-card">
        <div className="settings-card-header"><span className="settings-list-icon"><MonitorCog /></span><div><h2>Desktop layout</h2><p>Controls apply immediately and persist locally.</p></div></div>
        <SettingToggle label="Show top bar" description="Display the desktop quick-action appbar." checked={preferences.topbarVisible} onChange={(value) => setPreference("topbarVisible", value)} />
        <SettingSelect label="Collapse mode" description="Choose dynamic, always expanded, or collapsed navigation rail." value={preferences.sidebarCollapseMode} onChange={(value) => {
          setPreference("sidebarCollapseMode", value);
          setPreference("sidebarCollapsed", false);
          if (value === "expanded") setPreference("sidebarPinned", true);
        }}>
          <option value="dynamic">Dynamic</option><option value="expanded" disabled={preferences.sidebarBehavior === "overlay"}>Always expanded</option><option value="collapsed">Always collapsed</option>
        </SettingSelect>
        {preferences.sidebarCollapseMode === "dynamic" && <SettingToggle label="Pin dynamic sidebar" description="Keep the dynamic sidebar expanded until it is unpinned." checked={preferences.sidebarPinned} onChange={(value) => {
          setPreference("sidebarPinned", value);
          setPreference("sidebarCollapsed", false);
        }} />}
        <SettingSelect label="Sidebar behavior" description="Push content inline or expand over the compact rail." value={preferences.sidebarBehavior} onChange={(value) => {
          setPreference("sidebarBehavior", value);
          if (value === "overlay" && preferences.sidebarCollapseMode === "expanded") setPreference("sidebarCollapseMode", "dynamic");
        }}>
          <option value="inline">Inline (pushes content)</option><option value="overlay">Overlay (slides over)</option>
        </SettingSelect>
        <SettingSelect label="Sidebar display" description="Choose icons with labels, compact labels, or icons only." value={preferences.sidebarDisplayMode} onChange={(value) => setPreference("sidebarDisplayMode", value)}>
          <option value="icon-and-text-expanded">Icon and text</option><option value="icon-and-text-compact">Icon and text (compact)</option><option value="icon-only">Icon only</option><option value="text-only">Text only</option>
        </SettingSelect>
        <SettingSelect label="Navigation density" description="Adjust sidebar row height." value={preferences.navItemSize} onChange={(value) => setPreference("navItemSize", value)}>
          <option value="sm">Small</option><option value="default">Default</option><option value="lg">Large</option>
        </SettingSelect>
        <SettingSelect label="Top bar button size" description="Adjust desktop appbar control height." value={preferences.topbarItemSize} onChange={(value) => setPreference("topbarItemSize", value)}>
          <option value="sm">Small</option><option value="default">Default</option><option value="lg">Large</option>
        </SettingSelect>
        <SettingSelect label="Top bar display" description="Show icon labels or use compact icons." value={preferences.topbarDisplayMode} onChange={(value) => setPreference("topbarDisplayMode", value)}>
          <option value="icon-and-label">Icons and labels</option><option value="icon-only">Icons only</option>
        </SettingSelect>
      </section>
      <section className="settings-card">
        <div className="settings-card-header"><span className="settings-list-icon"><Smartphone /></span><div><h2>Mobile appbar</h2><p>Configure the touch navigation fixed to the bottom edge.</p></div></div>
        <SettingToggle label="Show mobile appbar" description="Keep primary actions within thumb reach." checked={preferences.mobileAppbarVisible} onChange={(value) => setPreference("mobileAppbarVisible", value)} />
        <SettingSelect label="Mobile bar size" description="Adjust bar height and icon size." value={preferences.mobileAppbarSize} onChange={(value) => setPreference("mobileAppbarSize", value)}>
          <option value="sm">Small</option><option value="default">Default</option><option value="lg">Large</option>
        </SettingSelect>
        <SettingSelect label="Visible module items" description="Limit module shortcuts before the persistent Menu button." value={String(preferences.mobileAppbarItemsCount)} onChange={(value) => setPreference("mobileAppbarItemsCount", Number(value))}>
          {Array.from({ length: 10 }, (_, index) => index + 1).map((count) => <option key={count} value={count}>{count}</option>)}
        </SettingSelect>
        <SettingSelect label="Mobile bar display" description="Show text labels or compact icons." value={preferences.mobileAppbarDisplayMode} onChange={(value) => setPreference("mobileAppbarDisplayMode", value)}>
          <option value="icon-and-label">Icons and labels</option><option value="icon-only">Icons only</option>
        </SettingSelect>
      </section>
      <button className="button button-secondary settings-reset-button" type="button" onClick={resetLayoutPreferences}><RotateCcw /> Reset layout preferences</button>
    </div>
  );
}
